import { useCallback, useEffect, useRef, useState } from "react";

import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import Divider from "@/components/Divider";
import RichText from "@/components/RichText";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION } from "@/graphql/accounts";
import useUserProfile from "@/hooks/useUserProfile";
import LifeStyleIcon from "@/images/LifeStyleIcon";
import { PanelBoxV2 } from "@/tpo/Boxes";
import LinkWrapper from "@/tpo/LinkWrapper";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import AddToBasketSection from "@/tpo/partnerDashboard/practitioner/AddToBasketSection";
import BiomarkersAndSymptomsList from "@/tpo/shop/BiomarkersAndSymptomsList";
import Carousel from "@/tpo/shop/Carousel";
import RelatedPdfs from "@/tpo/shop/RelatedFiles";
import RelatedFileClasses from "@/tpo/shop/RelatedFiles.module.css";
import TestProductInformationPanels from "@/tpo/shop/TestProductInformationPanels";
import { DISPLAY_CONTEXT_ALWAYS_VISIBLE, DISPLAY_CONTEXT_B2B_ONLY } from "@/tpo/shop/constants";
import getCarouselImages from "@/tpo/shop/testProductDetail/utils/getCarouselImages";
import ButtonV2, { ActionIcon, LinkButton } from "@/v2/Buttons";
import { useMutation } from "@apollo/client";
import { useTheme } from "styled-components";

import { CMP_PERMISSION_PURCHASE_PRODUCTS } from "../../constants";

export default function ReportDetail() {
  const { testProduct, selectTestProductId, selectTestProductOptionId } =
    useTestProductModalContext();
  const isStudentAllowed = !!testProduct?.isStudentAllowed;
  const studentRestrictionMessage = "Not available";
  const [showExistingResultsNotice, setShowExistingResultsNotice] = useState(false);

  const { organisation } = useOrganisationContext();

  const { basket, addProductToBasket, setProductQtyInOrganisationBasket } =
    useOrganisationBasketContext();

  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();
  const permissions = organisation?.permissions || [];

  const carouselImages = getCarouselImages(testProduct);

  const handleAddToBasket = useCallback(
    (product, option) => {
      if (!product?.isStudentAllowed) {
        return;
      }
      addProductToBasket(product.id, option?.id);
      if (basket?.multiplePatientsEnabled) {
        setBasketOpen(true);
        selectTestProductId(null);
        selectTestProductOptionId(null);
      }
    },
    [
      addProductToBasket,
      basket?.multiplePatientsEnabled,
      selectTestProductId,
      selectTestProductOptionId,
      setBasketOpen
    ]
  );

  useEffect(() => {
    setShowExistingResultsNotice(false);
  }, [testProduct?.id]);

  const practitionerSupportRequired = !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

  const informationPanels = (testProduct?.content?.informationPanels ?? []).filter(
    panel =>
      panel.displayContext === DISPLAY_CONTEXT_ALWAYS_VISIBLE ||
      panel.displayContext === DISPLAY_CONTEXT_B2B_ONLY
  );

  const onRequestAddToBasket = () => {
    setShowExistingResultsNotice(true);
  };

  const onContinueToPurchase = () => {
    handleAddToBasket(testProduct);
    setShowExistingResultsNotice(false);
  };

  const { patientProfile } = useUserProfile();

  const setQty = useCallback(
    qty => {
      setProductQtyInOrganisationBasket({
        productId: testProduct.id,
        quantity: qty
      });
    },
    [setProductQtyInOrganisationBasket, testProduct.id]
  );

  const sampleReports = [
    ...(testProduct.sampleLabReports ?? []),
    ...(testProduct.sampleGeneratedReports ?? [])
  ];

  const [toggleFavouriteMutation] = useMutation(TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION);

  const toggleFavourite = useCallback(
    testProductId => {
      toggleFavouriteMutation({
        variables: {
          testProductId
        }
      });
    },
    [toggleFavouriteMutation]
  );

  const theme = useTheme();

  const mainDetailsRef = useRef(null);
  const biomarkersAndSymptomsListRef = useRef(null);
  const informationPanelsRef = useRef(null);
  const downloadFilesRef = useRef(null);

  const favourite = patientProfile?.testProductFavouriteIds?.includes(testProduct.id);

  return (
    <>
      <Box display="none" data-component-name="TruncatedProductDetail" />
      <Group
        height={[43.2, 43.2, 131.2]}
        mt={[-43.2, -43.2, -131.2]}
        p={40}
        pr={[80, 80, 88]}
        gap={20}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
      >
        <LinkButton
          active
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => mainDetailsRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Overview
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() =>
            biomarkersAndSymptomsListRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          Biomarkers
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => informationPanelsRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Test Details
        </LinkButton>
        <Divider color="dark" vertical height="10px" />
        <LinkButton
          sx={{ letterSpacing: "2.8px" }}
          onClick={() => downloadFilesRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          Downloads
        </LinkButton>
      </Group>
      <PanelBoxV2
        maxWidth={768}
        outer={{
          bg: "white",
          pt: carouselImages.length ? 0 : 60,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40]
        }}
        stacked
        gap={[20, 20, 40]}
      >
        {!!carouselImages.length && (
          <Box bg="haze" p={20} mb={20}>
            <Carousel images={carouselImages} bg="haze" />
          </Box>
        )}
        <Box>
          <Box width="100%" maxWidth={640} mx="auto">
            <Group alignItems="center" gap={10}>
              {!!toggleFavourite && (
                <ActionIcon
                  size={["sm", "sm", "md"]}
                  bg="haze"
                  variant="circle"
                  onClick={toggleFavourite}
                >
                  <LifeStyleIcon
                    size={20}
                    outline={!favourite}
                    filled={favourite}
                    color={favourite ? theme.colors.genetics : "black"}
                  />
                </ActionIcon>
              )}
              <Box
                fontFamily="gilroyBold"
                fontSize={[18, 18, 28]}
                lineHeight="130%"
                data-testid="MainDetails:Name"
              >
                {testProduct.name}
              </Box>
            </Group>
            <Spacer py={[7.5, 7.5, 15]} />
            <RichText fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
              {testProduct.content.description}
            </RichText>
            {showExistingResultsNotice && (
              <Box
                mt={[40]}
                borderTopStyle="solid"
                borderTopWidth={1}
                borderTopColor="haze"
                pt={20}
                data-testid="reportDetail:existingResultsNotice"
              >
                <Box
                  bg="#FFF1F2"
                  p={[28]}
                  borderRadius={12}
                  borderStyle="solid"
                  borderColor="#FFE1E4"
                  borderWidth={1}
                >
                  <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]} lineHeight="130%">
                    This report needs existing results
                  </Box>
                  <Box
                    fontFamily="gilroyMedium"
                    fontSize={[14, 14, 16]}
                    lineHeight="150%"
                    mt={20}
                  >
                    Reports are generated from your patient&apos;s test data, so they&apos;ll need a
                    completed DNA test or a supported functional test on their account before the
                    report can be viewed.
                  </Box>
                  <Group mt={30} gap={[16, 16, 20]} alignItems="center" flexWrap="wrap">
                    <ButtonV2
                      color="green"
                      withChevron
                      onClick={onContinueToPurchase}
                      size={["sm", "sm", "md"]}
                    >
                      Continue to purchase
                    </ButtonV2>
                    <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]} textTransform="uppercase">
                      or
                    </Box>
                    <ButtonV2
                      withChevron
                      color="dark"
                      as={LinkWrapper}
                      to={`/cmp/organisations/${organisation.hashId}/dashboard/shop/tests?slug=dna-test`}
                      size={["sm", "sm", "md"]}
                    >
                      View DNA test
                    </ButtonV2>
                  </Group>
                </Box>
              </Box>
            )}
            {!showExistingResultsNotice && (
              <AddToBasketSection
                setQty={setQty}
                onAddToBasket={onRequestAddToBasket}
                disabled={!isStudentAllowed}
                disabledMessage={!isStudentAllowed ? studentRestrictionMessage : undefined}
                priceElement={
                  <Stack>
                    <Group gap={10}>
                      {testProduct.tradeFullPrice !== undefined &&
                        testProduct.tradeFullPrice > testProduct.tradeCurrentPrice && (
                          <GilroyFontCurrency
                            symbol={basket?.currencySymbol}
                            value={testProduct.tradeFullPrice}
                            color="dark"
                            strikethrough
                            data-testid="truncatedProductDetail:tradeFullPrice"
                          />
                        )}
                      <GilroyFontCurrency
                        symbol={basket?.currencySymbol}
                        value={testProduct.tradeCurrentPrice}
                        data-testid="truncatedProductDetail:tradeCurrentPrice"
                        color={
                          testProduct.tradeFullPrice !== undefined &&
                          testProduct.tradeFullPrice > testProduct.tradeCurrentPrice
                            ? "red"
                            : "dark"
                        }
                      />
                    </Group>
                    <GilroyFontCurrency
                      symbol={basket?.currencySymbol}
                      prefix="RRP"
                      color="midGrey"
                      fontSize={18}
                      decimalFontSize={18}
                      value={testProduct.exvatFullPrice}
                      data-testid="truncatedProductDetail:retailFullPrice"
                    />
                  </Stack>
                }
                practitionerSupportRequired={practitionerSupportRequired}
                status={testProduct.status}
              />
            )}
          </Box>
        </Box>
      </PanelBoxV2>
      {!!(
        testProduct.content.relatedSymptoms?.length || testProduct.content.biomarkersTested?.length
      ) && (
        <PanelBoxV2
          ref={biomarkersAndSymptomsListRef}
          maxWidth={1020}
          outer={{
            bg: "partners",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            "data-component-name": "TestProductDetail:BiomarkersAndSymptomsList"
          }}
        >
          <BiomarkersAndSymptomsList
            relatedSymptoms={testProduct.content.relatedSymptoms}
            biomarkersTested={testProduct.content.biomarkersTested}
          />
        </PanelBoxV2>
      )}
      <PanelBoxV2
        maxWidth={768}
        outer={{
          bg: "haze",
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          px: [20, 20, 40]
        }}
        stacked
        gap={40}
      >
        <TestProductInformationPanels panels={informationPanels} />
      </PanelBoxV2>
      {!!(
        sampleReports?.length ||
        testProduct.instructions?.length ||
        testProduct.supportingDocuments?.length
      ) && (
        <PanelBoxV2
          maxWidth={768}
          outer={{
            bg: "white",
            pt: theme.spacing.section.pt,
            pb: theme.spacing.section.pb,
            px: [20, 20, 40],
            className: RelatedFileClasses.container
          }}
        >
          <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
            Download files
          </Box>
          {!!sampleReports?.length && (
            <RelatedPdfs sectionName="Sample Reports" pdfs={sampleReports} />
          )}
          {!!testProduct.instructions?.length && (
            <RelatedPdfs sectionName="Instructions" pdfs={testProduct.instructions} />
          )}
          {!!testProduct.supportingDocuments?.length && (
            <RelatedPdfs
              sectionName="Supporting Documents"
              pdfs={testProduct.supportingDocuments}
            />
          )}
        </PanelBoxV2>
      )}
    </>
  );
}
